import React from 'react'
import ReactDOM from 'react-dom'
import { X } from 'lucide-react'
import { Button } from 'components/ui/Button'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  footer?: React.ReactNode
  hideCloseButton?: boolean
  /** Override overlay stacking when opening a modal above another modal */
  overlayClassName?: string
}

const sizeClassMap: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-4xl',
  xl: 'sm:max-w-6xl',
  full: 'sm:max-w-[95vw]',
}

export function Modal({ isOpen, onClose, title, children, className = '', size = 'md', footer, hideCloseButton = false, overlayClassName = '' }: ModalProps) {
  if (!isOpen) return null
  const sizeClass = sizeClassMap[size] || sizeClassMap.md

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 flex items-center justify-center p-4 sm:p-6 ${overlayClassName || 'z-[100]'}`}>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={hideCloseButton ? undefined : onClose}
      />
      
      {/* Modal */}
      <div
        className={`
          relative w-full max-w-full bg-background border shadow-lg flex flex-col
          rounded-none sm:rounded-lg max-h-[calc(100vh-2rem)] sm:max-h-[90vh] overflow-hidden
          ${sizeClass} ${className}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
          {!hideCloseButton && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="border-t bg-background p-4 sm:p-6 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
